// src/hooks/useRequestRide.ts
// Hook para manejar la solicitud de viaje (crear orden + publicar en changas_board)

import { useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { calculateDeliveryFee, type LatLng } from '@/lib/delivery';

// Re-export LatLng para uso en componentes
export type { LatLng } from '@/lib/delivery';

export interface RequestRidePayload {
  transportType: string; // 'auto' | 'moto' | 'bicicleta'
  serviceType: string; // 'persona' | 'paqueteria'
  pickupAddress: string; // Dirección legible
  deliveryAddress: string; // Dirección legible
  pickupCoords: LatLng; // { lat, lng }
  deliveryCoords: LatLng; // { lat, lng }
  pickupSalutation?: string | null;
  pickupFirstName?: string | null;
  pickupLastName?: string | null;
  deliverySalutation?: string | null;
  deliveryFirstName?: string | null;
  deliveryLastName?: string | null;
  deliveryFee?: number | null; // Precio del vehículo seleccionado
}

export interface RequestRideResult {
  success: boolean;
  orderId?: string;
  distance?: number;
  fee?: number;
  message?: string;
}

export function useRequestRide() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const normalizeLatLng = (lat: unknown, lng: unknown): LatLng | null => {
    const parsedLat = typeof lat === 'number' ? lat : Number(lat);
    const parsedLng = typeof lng === 'number' ? lng : Number(lng);
    if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) return null;
    return { lat: parsedLat, lng: parsedLng };
  };

  /**
   * Calcular precio y distancia usando OpenRouteService
   */
  const calculateFee = async (
    pickupCoords: LatLng,
    deliveryCoords: LatLng,
    transportType: string = 'auto',
    serviceType: string = 'persona'
  ): Promise<{ distanceKm: number; durationMin: number; fee: number; basePrice: number; pricePerKm: number } | null> => {
    try {
      const result = await calculateDeliveryFee(pickupCoords, deliveryCoords, transportType, serviceType);
      return {
        distanceKm: result.distanceKm,
        durationMin: result.durationMin,
        fee: result.fee,
        basePrice: result.basePrice,
        pricePerKm: result.pricePerKm
      };
    } catch (err) {
      console.error('[useRequestRide] Error calculating fee:', err);
      return null;
    }
  };

  /**
   * Crear orden + publicar en changas_board
   */
  const requestRide = async (
    userId: string,
    payload: RequestRidePayload
  ): Promise<RequestRideResult> => {
    setLoading(true);
    setError(null);

    try {
      const pickupCoords = normalizeLatLng(payload.pickupCoords?.lat, payload.pickupCoords?.lng);
      const deliveryCoords = normalizeLatLng(payload.deliveryCoords?.lat, payload.deliveryCoords?.lng);

      if (!pickupCoords || !deliveryCoords) {
        throw new Error('Coordenadas de retiro o destino inválidas');
      }

      const pricingServiceType = payload.serviceType === 'driver'
        ? 'persona'
        : payload.serviceType === 'courier'
          ? 'paquete'
          : payload.serviceType;

      const pricingTransportTypeMap: Record<string, string> = {
        car: 'auto',
        motorcycle: 'moto',
        bike: 'bici',
      };
      const pricingTransportType = pricingTransportTypeMap[payload.transportType] || payload.transportType;

      // 1. Calcular distancia y tarifa
      const feeData = await calculateDeliveryFee(
        pickupCoords,
        deliveryCoords,
        pricingTransportType,
        pricingServiceType
      );

      if (!feeData) {
        throw new Error('No se pudo calcular la tarifa');
      }


      // 2. Obtener city_id desde las coordenadas del punto de retiro
      let cityId = '';
      
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${pickupCoords.lat}&lon=${pickupCoords.lng}&zoom=10&addressdetails=1`,
          { 
            mode: 'cors',
            headers: {
              'User-Agent': 'GoldenLobsterHop/1.0'
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          // Intentar obtener ciudad, si no existe usar estado o país
          const city = data.address?.city || 
                      data.address?.town || 
                      data.address?.village ||
                      data.address?.state ||
                      data.address?.country ||
                      'unknown';
          
          // Normalizar el nombre de ciudad (sin acentos, minúsculas, sin espacios)
          cityId = city
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '_');
          
        }
      } catch (err) {
        console.warn('[useRequestRide] ⚠️ No se pudo obtener ciudad, usando default');
      }
      
      // Si no se pudo obtener, usar 'unknown'
      if (!cityId) {
        cityId = 'unknown';
      }

      // Use payload.deliveryFee if provided, otherwise use calculated fee.
      // Light is shown with discount in UI, so keep parity if payload omitted fee.
      const fallbackFee = payload.transportType === 'light' ? feeData.fee * 0.7 : feeData.fee;
      const finalDeliveryFee = Math.round(payload.deliveryFee ?? fallbackFee);

      // 3. Crear orden
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .insert({
          user_id: userId,
          city_id: cityId,
          delivery_pickup_address: payload.pickupAddress,
          delivery_address: payload.deliveryAddress,
          delivery_pickup_latitude: pickupCoords.lat,
          delivery_pickup_longitude: pickupCoords.lng,
          delivery_latitude: deliveryCoords.lat,
          delivery_longitude: deliveryCoords.lng,
          delivery_distance: feeData.distanceKm,
          delivery_fee: finalDeliveryFee,
          required_transport_type: payload.transportType,
          required_service_type: payload.serviceType,
          dispatch_status: 'published', // Publicar inmediatamente
          status: 'pending',
          total: finalDeliveryFee,
        })
        .select('id')
        .single();

      if (orderError) {
        console.error('[useRequestRide] Error creating order:', orderError);
        throw orderError;
      }

      if (!orderData?.id) {
        throw new Error('No order ID returned');
      }

      // Defensive sync: if any DB side effect changed fee/total, force the selected value.
      const { data: persistedOrder } = await supabase
        .from('orders')
        .select('delivery_fee, total, required_transport_type')
        .eq('id', orderData.id)
        .maybeSingle();

      const persistedFee = Number(persistedOrder?.delivery_fee ?? NaN);
      const persistedTotal = Number(persistedOrder?.total ?? NaN);
      const feeMismatch = Number.isFinite(persistedFee) ? Math.round(persistedFee) !== finalDeliveryFee : true;
      const totalMismatch = Number.isFinite(persistedTotal) ? Math.round(persistedTotal) !== finalDeliveryFee : true;
      const transportMismatch = persistedOrder?.required_transport_type !== payload.transportType;

      if (feeMismatch || totalMismatch || transportMismatch) {
        const { error: forceSyncError } = await supabase
          .from('orders')
          .update({
            delivery_fee: finalDeliveryFee,
            total: finalDeliveryFee,
            required_transport_type: payload.transportType,
          })
          .eq('id', orderData.id);

        if (forceSyncError) {
          console.warn('[useRequestRide] Could not force-sync persisted fee/transport:', forceSyncError);
        }
      }


      // 4. Esperar para asegurar que el trigger se ejecute
      await new Promise(resolve => setTimeout(resolve, 500));

      // 5. Ofertar manualmente como respaldo (en caso de que el trigger no funcione)
      const { data: offerResult, error: offerError } = await supabase.rpc(
        'offer_changa_to_best_courier',
        { p_order_id: orderData.id }
      );

      if (offerError) {
        console.warn('[useRequestRide] ⚠️ Error en oferta:', offerError);
        // No fallar si la oferta no funciona, la orden ya está creada
      } else if (offerResult?.success) {
      } else {
        console.warn('[useRequestRide] ℹ️ Oferta completada pero:', offerResult?.message);
      }

      setLoading(false);
      return {
        success: true,
        orderId: orderData.id,
        distance: feeData.distanceKm,
        fee: finalDeliveryFee,
        message: 'Viaje solicitado exitosamente',
      };
    } catch (err: any) {
      console.error('[useRequestRide] Error:', err);
      const msg =
        err?.message ||
        err?.details ||
        'Error al solicitar el viaje. Intenta de nuevo.';
      setError(msg);
      setLoading(false);
      return {
        success: false,
        message: msg,
      };
    }
  };

  return {
    loading,
    error,
    calculateFee,
    requestRide,
  };
}
